/**
 * ─────────────────────────────────────────────────────────────────────────────
 * Global auth / session-expiry handler (Axios response interceptor)
 * ─────────────────────────────────────────────────────────────────────────────
 * Every thunk in this app talks to the backend through the default `axios`
 * instance, so a single response interceptor here centralises session handling:
 *
 *   Any authenticated request that returns 401 / "Invalid token" / "Token
 *   expired" / "JWT expired" / "Authentication failed" ⇒ the session is dead.
 *   We then, EXACTLY ONCE (even when many requests fail at the same time):
 *     1. Clear redux auth state + persisted storage (token can't rehydrate).
 *     2. Set a one-shot flag so the login page shows a single message.
 *     3. Full-navigate to /login — this also aborts every in-flight request and
 *        clears the transient per-request error toasts, so only ONE message
 *        remains ("Your session has expired. Please log in again.").
 *
 * Login (`/api/auth/login`) is exempt: a 401 there is a wrong-credentials
 * validation result, not an expired session. Public endpoints are unaffected
 * because they never return an auth failure.
 * ─────────────────────────────────────────────────────────────────────────────
 */

import axios from "axios";
import { store, persistor } from "@/store";
import { logout } from "@/components/features/slices/authSlice";

// Guards so a burst of simultaneous 401s triggers exactly ONE logout + redirect.
let isLoggingOut = false;
let installed = false;

const SESSION_EXPIRED_FLAG = "travov_session_expired";

// A 401 on these endpoints is normal validation (e.g. wrong password) — never
// force-logout for them.
const isAuthExemptUrl = (url = "") => url.includes("/api/auth/login");

// Dead session ⇒ any 401, or a message that names an auth failure.
const isSessionExpired = (error) => {
    if (error?.response?.status === 401) return true;
    const msg = String(error?.response?.data?.message || error?.message || "").toLowerCase();
    return /invalid token|token expired|jwt expired|authentication failed|session expired|unauthorized|not authorized/.test(msg);
};

const forceLogout = () => {
    if (isLoggingOut) return; // only once per expiry storm
    isLoggingOut = true;

    // 1. Clear redux + persisted auth so a reload can't rehydrate the dead token.
    try { store.dispatch(logout()); } catch (_) {}
    try { persistor.purge(); } catch (_) {}
    try {
        localStorage.removeItem("persist:auth");
        localStorage.removeItem("persist:user");
        sessionStorage.clear();
        // 2. Read once on the login page → shows exactly ONE session message.
        localStorage.setItem(SESSION_EXPIRED_FLAG, "1");
    } catch (_) {}

    // 3. Full navigation to login: stops any further protected requests and
    //    clears the transient error toasts from the failed calls. `replace`
    //    avoids a back-button loop; the path guard avoids redirecting on /login.
    if (typeof window !== "undefined" && window.location.pathname !== "/login") {
        window.location.replace("/login");
    }
};

// Attach the interceptor to the default axios instance exactly once.
export const installAuthInterceptor = () => {
    if (installed) return;
    installed = true;
    axios.interceptors.response.use(
        (response) => response,
        (error) => {
            const url = error?.config?.url || "";
            if (isSessionExpired(error) && !isAuthExemptUrl(url)) {
                forceLogout();
            }
            return Promise.reject(error);
        }
    );
};

// Login page calls this on mount: returns true once after a session-expiry
// redirect so it can show the single "session expired" toast.
export const consumeSessionExpiredFlag = () => {
    try {
        if (localStorage.getItem(SESSION_EXPIRED_FLAG)) {
            localStorage.removeItem(SESSION_EXPIRED_FLAG);
            return true;
        }
    } catch (_) {}
    return false;
};
